import type { AnswerWidgets } from '@openx/components-core/lib/AiChat/answerWidgets';
import { type Message, MessageRole } from '@openx/components-core/lib/AiChat/types';
import { isRecord } from '@openx/components-core/lib/Markdown/widgets/answerWidgetPayload';

// 'openx' is the provider of the troubleshooting answers; the Targeting Explainer mock (PR #9240) names one per kind
const ANSWER_WIDGET_PROVIDERS: readonly string[] = ['openx', 'targeting-summary', 'targeting-personas'];

const readAnswerWidget = (message: Message): { id: string; payload: unknown } | null => {
  if (message.role !== MessageRole.WIDGET || !isRecord(message.widget)) return null;

  const { id, provider, payload } = message.widget;
  if (typeof provider !== 'string' || !ANSWER_WIDGET_PROVIDERS.includes(provider)) return null;
  if (typeof id !== 'string' || id.length === 0) return null;

  return { id, payload: payload ?? null };
};

const collectFollowingWidgets = (messages: Message[], answerIndex: number): AnswerWidgets => {
  const widgets: AnswerWidgets = {};

  for (const message of messages.slice(answerIndex + 1)) {
    if (message.role !== MessageRole.WIDGET) break;

    const widget = readAnswerWidget(message);
    if (widget !== null) widgets[widget.id] = widget.payload;
  }

  return widgets;
};

/**
 * Answer widgets per agent answer, keyed by the answer's `invocation_id`. A widget belongs to the answer of its
 * invocation, as in the troubleshooting chat. Until the real API is known, an answer whose invocation carries no
 * widgets takes the widget messages right after it, as the Targeting Explainer mock gives each widget its own id.
 */
export const collectOpenXAgentAnswerWidgets = (messages: Message[]): Map<string, AnswerWidgets> => {
  const byInvocation = new Map<string, AnswerWidgets>();

  for (const message of messages) {
    const widget = readAnswerWidget(message);
    if (widget === null || !message.invocation_id) continue;

    const widgets = byInvocation.get(message.invocation_id) ?? {};
    widgets[widget.id] = widget.payload;
    byInvocation.set(message.invocation_id, widgets);
  }

  messages.forEach((message, index) => {
    if (message.role !== MessageRole.AGENT || byInvocation.has(message.invocation_id)) return;

    const following = collectFollowingWidgets(messages, index);
    if (Object.keys(following).length > 0) byInvocation.set(message.invocation_id, following);
  });

  return byInvocation;
};
